<script setup>
import { stripBlockTags } from '@/composables/helpers/html';

const props = defineProps({
    headline: {
        type: String,
        required: false,
        default: '',
    },
    quoteCards: {
        type: Array,
        required: false,
        default: () => [],
    }
});

const scrollRef = ref(null);
const currentIndex = ref(0);

const isFirst = computed(() => currentIndex.value === 0);
const isLast = computed(() => currentIndex.value === (props.quoteCards?.length || 1) - 1);

const goNext = () => {
    if (!scrollRef.value || isLast.value) return;
    const card = scrollRef.value.children[currentIndex.value + 1];
    card?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
};

const goPrev = () => {
    if (!scrollRef.value || isFirst.value) return;
    const card = scrollRef.value.children[currentIndex.value - 1];
    card?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
};

const onScroll = () => {
    if (!scrollRef.value) return;
    const container = scrollRef.value;
    const scrollLeft = container.scrollLeft;
    const cardWidth = container.children[0]?.offsetWidth || 1;
    currentIndex.value = Math.round(scrollLeft / cardWidth);
};
</script>

<template>
    <section class="block-quote-carousel relative overflow-clip py-[60px] lg:pt-[90px] lg:pb-[60px]"
        tabindex="-1"
        style="background: linear-gradient(157.3deg, #540748 23.53%, var(--purple) 58.67%, #96167F 79.84%)">

        <div class="relative z-[2] flex flex-col items-center gap-[29px]">
            <!-- Scroll-snap carousel -->
            <div ref="scrollRef" @scroll="onScroll"
                class="flex overflow-x-auto snap-x snap-mandatory w-full scrollbar-hide"
                style="-webkit-overflow-scrolling: touch; scrollbar-width: none;">
                <div v-for="(card, i) in quoteCards" :key="i"
                    class="snap-center shrink-0 w-full flex flex-col items-center justify-center px-(--box-padding-lr-m) lg:px-0">
                    <!-- Quote mark -->
                    <svg width="32" height="26" viewBox="0 0 32 26" fill="none" aria-hidden="true" class="mb-[29px]">
                        <path d="M29.2987 0.017294C30.7532 -0.196553 31.5844 1.62116 29.8182 2.79734C26.3896 5.04275 24.5195 8.03664 23.2727 12.8483C23.2727 12.8483 24.1039 12.3136 26.0779 12.3136C29.5065 12.3136 32 15.4145 32 19.0499C32 22.7923 29.1948 26 25.3506 26C20.6753 26 18.0779 21.723 18.0779 15.5214C18.0779 7.60895 24.1039 0.872693 29.2987 0.017294ZM11.2208 0.017294C12.6753 -0.196553 13.5065 1.62116 11.7403 2.79734C8.3117 5.04275 6.44156 8.03664 5.1948 12.8483C5.1948 12.8483 6.02598 12.3136 8 12.3136C11.4286 12.3136 13.9221 15.4145 13.9221 19.0499C13.9221 22.7923 11.1169 26 7.27273 26C2.59741 26 0 21.723 0 15.5214C0 7.60895 6.02599 0.872693 11.2208 0.017294Z" fill="#FCC3DC"/>
                    </svg>
                    <blockquote class="font-hubspot-serif font-normal text-white text-center text-[calc(20/var(--base-fs)*1rem)] leading-[1.3] tracking-normal md:text-[calc(30/var(--base-fs)*1rem)] max-w-[900px]"
                        v-html="stripBlockTags(card.quote)" />
                    <div class="flex flex-col md:flex-row justify-center items-center gap-1 md:gap-2.5 text-white font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] pt-[29px]">
                        <span v-if="card.attribution">{{ card.attribution }}</span>
                        <svg v-if="card.attribution && card.attributedTitle" class="hidden md:display-block" width="5" height="6" viewBox="0 0 5 6" fill="none" aria-hidden="true">
                            <path d="m4.98236 3.50784c-.03981-.20647-.10584-.43008-.15802-.63555-.16163-.63664-.33361-1.27131-.50283-1.905436-.03015-.069966-.07614-.128601-.12258-.186614-.26692-.18877-.57044-.318797-.8562-.475435-.21762-.119206-.46388-.3200767-.7153-.30388276-.2335.01506066-.64988.28077976-.87647.39486776-.34069.171517-.68481.335869-1.023983.510785-.170325.087805-.361481.176815-.524485.275255-.2977849.17978-.19634607.3816-.1351253.68216.1528973.75069.3206443 1.5268.4990823 2.27066.054139.22565.128629.36112.339481.44992.29125.12264.65775.22989.962.32603.26881.08495.46227.18263.73991.06785.51847-.2144 1.05423-.47661 1.56465-.71725.16022-.07552.68368-.29566.77168-.41786.07338-.10192.06098-.21729.03819-.3355z" fill="#FCC3DC"/>
                        </svg>
                        <span v-if="card.attributedTitle">{{ card.attributedTitle }}</span>
                    </div>
                </div>
            </div>

            <!-- Pill arrow controls -->
            <div v-if="quoteCards?.length > 1" class="flex w-[76px] gap-[16px] items-center justify-center px-[16px] py-[7px] h-[42px]
                rounded-[40px] border border-[#fcc3dc] mt-[10px]">
                <button type="button" aria-label="Previous quote"
                    :disabled="isFirst"
                    :class="['group shrink-0 size-[13px] bg-transparent border-none p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-sm', isFirst ? 'opacity-50 cursor-default' : 'cursor-pointer']"
                    @click="goPrev">
                    <svg viewBox="0 0 14.5 14.5" fill="none" aria-hidden="true" class="size-full">
                        <path d="M13.75 7.25H0.75M0.75 7.25L6.88889 0.750002M0.75 7.25L6.88889 13.75"
                            stroke="#fcc3dc" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                            class="transition-[stroke] duration-200 group-hover:stroke-white" />
                    </svg>
                </button>
                <button type="button" aria-label="Next quote"
                    :disabled="isLast"
                    :class="['group shrink-0 size-[13px] bg-transparent border-none p-0 -scale-x-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-sm', isLast ? 'opacity-50 cursor-default' : 'cursor-pointer']"
                    @click="goNext">
                    <svg viewBox="0 0 14.5 14.5" fill="none" aria-hidden="true" class="size-full">
                        <path d="M13.75 7.25H0.75M0.75 7.25L6.88889 0.750002M0.75 7.25L6.88889 13.75"
                            stroke="#fcc3dc" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                            class="transition-[stroke] duration-200 group-hover:stroke-white" />
                    </svg>
                </button>
            </div>
        </div>

        <!-- Shard decoration -->
        <img src="/img/2026/testimonials/testimonial-shard.png" loading="lazy" decoding="async" alt="" aria-hidden="true"
            class="absolute bottom-0 right-0 z-[1] pointer-events-none w-[500px] max-w-[50%]" />
    </section>
</template>

<style>
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
</style>
